/**
 * Identification at save time (ENG-4833 + ENG-4832 persistence).
 *
 * Pure adapters between the add store and the two save outputs:
 *   - the six analytics props on `device_add` / `multi_add_device_added`
 *     (through `resolveIdentificationProps`, the single source of their rules);
 *   - the StudioDevice `typed_brand` / `typed_model` / `identified_by` fields,
 *     carried on the candidate as `typedBrand` / `typedModel` / `identifiedBy`.
 */
import { resolveIdentificationProps } from '#~/analytics/domains/device-identification.js';
import type {
  DeviceIdentificationModel,
  DeviceIdentificationProperties,
  DeviceIdentificationState
} from '#~/analytics/domains/device-identification.js';
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Collection } from '#~/api/device/registry/index.js';
import type { Template } from '#~/device/registry/index.js';
import { resolveDetectedCamera } from './utils.js';
import type { DetectionSource } from './utils.js';

/** Device fields persisted to StudioDevice fields 13–15. */
export type PersistedIdentification = {
  typedBrand?: string;
  typedModel?: string;
  identifiedBy?: string;
};

/**
 * The identification fields to persist with the device. Only set values are
 * returned: an absent field stays absent on the wire, the proto's "not
 * recorded" (legacy records read the same way).
 */
export const toPersistedIdentification = (
  identification: T_Maybe<DeviceIdentificationState>
): PersistedIdentification => ({
  ...(identification?.typedBrand ? { typedBrand: identification.typedBrand } : {}),
  ...(identification?.typedModel ? { typedModel: identification.typedModel } : {}),
  ...(identification?.identifiedBy ? { identifiedBy: identification.identifiedBy } : {})
});

/** Registry facts the resolver reads. */
const toIdentificationModel = (model: Model): DeviceIdentificationModel => ({
  generic: model.generic,
  notYetSupported: model.notYetSupported
});

/**
 * Single add: props for `device_add`. The registry model counts only when it
 * is the model actually being saved — a template left over from an earlier
 * selection would misreport the driver.
 */
export const resolveSingleAddIdentification = ({
  identification,
  modelId,
  model,
  hasControl
}: {
  identification: T_Maybe<DeviceIdentificationState>;
  modelId: T_Maybe<Model['id']>;
  model: T_Maybe<Model>;
  hasControl: boolean;
}): DeviceIdentificationProperties =>
  resolveIdentificationProps({
    identification: identification ?? {},
    model: model && modelId && model.id === modelId ? toIdentificationModel(model) : undefined,
    hasControl
  });

/**
 * Multi-add row: rows skip the brand/model step, so nobody searched (ENG-4833).
 * `identified_by` is therefore derived from what discovery resolved
 * (`runBulkCommit`, wiring.ts):
 *   - model resolved (`discovery.modelId`, committed on its template) → `detected`;
 *   - brand only (`discovery.brandId`, committed on the brand's generic
 *     `otherModelId`) → `detected` too — detection exists and no search
 *     happened, so `brand_detected_then_searched` would be wrong;
 *   - nothing detected (plain RTSP / unmatched ONVIF, committed model-less) →
 *     `not_listed`;
 *   - a detected model the registry marks not yet supported →
 *     `not_yet_supported_picked` (ENG-4942). The multi-add "uses basic controls"
 *     dialog named it as not supported and the user continued — the multi-add
 *     counterpart of single-add's C2 Continue for a recognized camera, which
 *     records the same value (`notListedIdentifiedBy('camera_reported')`). So a
 *     recognized-but-unsupported camera never reads as an ordinary detection;
 *     its `driver_type` is `generic` either way.
 * Detected names come from the row's own match; the driver from the model the
 * row is committed with (its template, or the brand-generic fallback).
 */
export const resolveBulkAddIdentification = ({
  discovery,
  savedModelId,
  hasControl,
  templates,
  brandDetected,
  brands
}: {
  discovery: DetectionSource;
  savedModelId: T_Maybe<Model['id']>;
  hasControl: boolean;
  templates: T_Maybe<Record<Model['id'], T_Maybe<Template>>>;
  brandDetected: T_Maybe<
    Record<Brand['id'], T_Maybe<{ otherModelId: Model['id']; template: T_Maybe<Template> }>>
  >;
  brands: T_Maybe<Collection<Brand>>;
}): DeviceIdentificationProperties => {
  const matchedTemplate = discovery.modelId ? templates?.[discovery.modelId] : undefined;
  const detected = resolveDetectedCamera(discovery, matchedTemplate, brands);

  const brandGeneric = discovery.brandId ? brandDetected?.[discovery.brandId] : undefined;
  const savedTemplate = savedModelId
    ? (templates?.[savedModelId] ??
      (brandGeneric?.otherModelId === savedModelId ? brandGeneric.template : undefined))
    : undefined;

  return resolveIdentificationProps({
    identification: {
      // `resolveDetectedCamera` is undefined only when discovery matched
      // neither a model nor a brand (see the rule in the doc comment above).
      identifiedBy: !detected
        ? 'not_listed'
        : matchedTemplate?.model.id === discovery.modelId &&
            matchedTemplate?.model.notYetSupported === true
          ? 'not_yet_supported_picked'
          : 'detected',
      detectedBrand: detected?.brand,
      detectedModel: detected?.model
    },
    model: savedTemplate ? toIdentificationModel(savedTemplate.model) : undefined,
    hasControl
  });
};
